/**
 * Auth Page - Login and Registration
 */
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import SwissPostAutocomplete from "@/components/SwissPostAutocomplete";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import {
  Briefcase, User, Eye, EyeOff, Loader2, Check, ChevronRight, ChevronLeft,
  Phone, Building, MapPin, Camera, Award
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Swiss Cantons
const SWISS_CANTONS = [
  { code: "AG", name: "Aargau" }, { code: "AI", name: "Appenzell Innerrhoden" },
  { code: "AR", name: "Appenzell Ausserrhoden" }, { code: "BE", name: "Bern" },
  { code: "BL", name: "Basel-Landschaft" }, { code: "BS", name: "Basel-Stadt" },
  { code: "FR", name: "Fribourg" }, { code: "GE", name: "Genève" },
  { code: "GL", name: "Glarus" }, { code: "GR", name: "Graubünden" },
  { code: "JU", name: "Jura" }, { code: "LU", name: "Luzern" },
  { code: "NE", name: "Neuchâtel" }, { code: "NW", name: "Nidwalden" },
  { code: "OW", name: "Obwalden" }, { code: "SG", name: "St. Gallen" },
  { code: "SH", name: "Schaffhausen" }, { code: "SO", name: "Solothurn" },
  { code: "SZ", name: "Schwyz" }, { code: "TG", name: "Thurgau" },
  { code: "TI", name: "Ticino" }, { code: "UR", name: "Uri" },
  { code: "VD", name: "Vaud" }, { code: "VS", name: "Valais" },
  { code: "ZG", name: "Zug" }, { code: "ZH", name: "Zürich" }
];

// Icon map for categories
const iconMap = {
  'Briefcase': Briefcase
};

// Logo Component
const Logo = ({ className = "h-10 w-10" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_f315b95a-0efe-40b5-9cbe-765ee1480209/artifacts/fawkrmzt_logo%20512%20.512.png" 
    alt="Infirmly" 
    className={className}
  />
);

const AuthPage = ({ mode = "login", useAuth, useLanguage }) => {
  const [isLogin, setIsLogin] = useState(mode === "login");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [profilePreview, setProfilePreview] = useState(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    email: "", password: "", name: "", role: "client",
    phone: "", company_name: "", address: "", city: "",
    postal_code: "", canton: "", selected_categories: [],
    certifications: "", profile_picture: null, acceptedTerms: false
  });
  
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { t, language } = useLanguage();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const role = params.get('role');
    if (role) setFormData(prev => ({ ...prev, role }));
    axios.get(`${API}/categories`).then(res => setCategories(res.data)).catch(console.error);
  }, [location]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Dateigröße darf 5MB nicht überschreiten");
        return;
      }
      setFormData({ ...formData, profile_picture: file });
      const reader = new FileReader();
      reader.onloadend = () => setProfilePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const toggleCategory = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      selected_categories: prev.selected_categories.includes(categoryId)
        ? prev.selected_categories.filter(id => id !== categoryId)
        : [...prev.selected_categories, categoryId]
    }));
  };

  const handleStep1Submit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }
    if (formData.password.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen haben");
      return;
    }
    if (!formData.acceptedTerms) {
      toast.error("Bitte akzeptieren Sie die AGB");
      return;
    }
    if (formData.role === "client") {
      handleFinalSubmit();
    } else {
      setStep(2);
    }
  };

  const handleFinalSubmit = async () => {
    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('email', formData.email);
      submitData.append('password', formData.password);
      submitData.append('name', formData.name);
      submitData.append('role', formData.role);
      submitData.append('phone', formData.phone);
      submitData.append('language', language || 'de');
      
      if (formData.role === 'provider') {
        submitData.append('company_name', formData.company_name);
        submitData.append('address', formData.address);
        submitData.append('city', formData.city);
        submitData.append('postal_code', formData.postal_code);
        submitData.append('canton', formData.canton);
        submitData.append('categories', JSON.stringify(formData.selected_categories));
        submitData.append('certifications', formData.certifications);
        if (formData.profile_picture) {
          submitData.append('profile_picture', formData.profile_picture);
        }
      }

      const res = await axios.post(`${API}/auth/register`, submitData, { 
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      login(res.data.user, res.data.token);
      toast.success(t('welcomeToInfirmly'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ein Fehler ist aufgetreten");
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Debug: Show API URL being used
    const apiUrl = `${API}/auth/login`;
    console.log('Attempting login to:', apiUrl);
    
    try {
      const res = await fetch(apiUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email.trim().toLowerCase(),
          password: formData.password
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        // Server returned an error
        const errorMsg = data.detail || `Server Error: ${res.status}`;
        console.error('Login failed:', res.status, data);
        toast.error(errorMsg);
        return;
      }
      
      if (data && data.user && data.token) {
        login(data.user, data.token);
        toast.success("Erfolgreich angemeldet!");
        navigate('/dashboard');
      } else {
        toast.error("Ungültige Serverantwort");
      }
    } catch (error) {
      console.error('Login error:', error);
      // Show detailed error for debugging
      const errorMessage = error.message || 'Unknown error';
      const errorType = error.name || 'Error';
      
      // Alert for mobile debugging - shows exactly what went wrong
      alert(`DEBUG - Login Error:\nType: ${errorType}\nMessage: ${errorMessage}\nAPI: ${apiUrl}`);
      
      toast.error("Verbindungsfehler - " + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Login Form
  if (isLogin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-3 mb-6">
              <Logo className="h-12 w-12" />
              <span className="font-bold text-2xl" style={{ fontFamily: 'Chivo, sans-serif' }}>Infirmly Swiss</span>
            </Link>
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t('welcomeBack')}
            </h1>
            <p className="text-gray-500 mt-2">{t('loginToAccount')}</p>
          </div>

          <Card className="border-gray-200 shadow-sm">
            <CardContent className="p-6">
              <form onSubmit={handleLoginSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">{t('email')}</Label>
                  <Input
                    id="email" type="email" placeholder="max@beispiel.ch"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 h-11" required data-testid="email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="password">{t('password')}</Label>
                  <div className="relative mt-1">
                    <Input
                      id="password" type={showPassword ? "text" : "password"} placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="h-11 pr-10" required data-testid="password-input"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <div className="flex justify-end mt-2">
                    <Link to="/forgot-password" className="text-sm text-[#0044CC] hover:underline" data-testid="forgot-password-link">
                      Passwort vergessen?
                    </Link>
                  </div>
                </div>
                <Button type="submit" className="w-full h-11 bg-[#0044CC] hover:bg-[#003399] text-white font-medium rounded-full"
                  disabled={loading} data-testid="submit-btn">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('login')}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center mt-6 text-gray-500">
            {t('noAccount')}
            <button onClick={() => setIsLogin(false)} className="ml-1 text-[#0044CC] hover:underline font-medium">
              {t('register')}
            </button>
          </p>
        </div>
      </div>
    );
  }

  // Registration Form - Step 1
  if (step === 1) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-3 mb-6">
              <Logo className="h-12 w-12" />
              <span className="font-bold text-2xl" style={{ fontFamily: 'Chivo, sans-serif' }}>Infirmly Swiss</span>
            </Link>
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t('createAccount')}
            </h1>
            <p className="text-gray-500 mt-2">{t('registerFree')}</p>
            <div className="flex items-center justify-center gap-2 mt-4">
              <div className="w-8 h-8 rounded-full bg-[#0044CC] text-white flex items-center justify-center text-sm font-medium">1</div>
              <div className="w-12 h-1 bg-gray-200 rounded"></div>
              <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-400 flex items-center justify-center text-sm font-medium">2</div>
            </div>
          </div>

          <Card className="border-gray-200 shadow-sm">
            <CardContent className="p-6">
              <form onSubmit={handleStep1Submit} className="space-y-4">
                <div>
                  <Label htmlFor="name">{t('name')} *</Label>
                  <Input id="name" placeholder="Max Mustermann" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 h-11" required data-testid="register-name-input" />
                </div>
                <div>
                  <Label htmlFor="email">{t('email')} *</Label>
                  <Input id="email" type="email" placeholder="max@beispiel.ch" value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 h-11" required data-testid="email-input" />
                </div>
                <div>
                  <Label htmlFor="password">{t('password')} *</Label>
                  <div className="relative mt-1">
                    <Input id="password" type={showPassword ? "text" : "password"} placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="h-11 pr-10" required data-testid="password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>{t('iAm')} *</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <button type="button" onClick={() => setFormData({ ...formData, role: 'client' })}
                      className={`p-4 rounded-lg border-2 transition-all ${formData.role === 'client' ? 'border-[#0044CC] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                      data-testid="role-client">
                      <User className={`w-8 h-8 mx-auto mb-2 ${formData.role === 'client' ? 'text-[#0044CC]' : 'text-gray-400'}`} />
                      <p className={`text-sm font-medium ${formData.role === 'client' ? 'text-[#0044CC]' : 'text-gray-600'}`}>{t('client')}</p>
                    </button>
                    <button type="button" onClick={() => setFormData({ ...formData, role: 'provider' })}
                      className={`p-4 rounded-lg border-2 transition-all ${formData.role === 'provider' ? 'border-[#0044CC] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                      data-testid="role-provider">
                      <Briefcase className={`w-8 h-8 mx-auto mb-2 ${formData.role === 'provider' ? 'text-[#0044CC]' : 'text-gray-400'}`} />
                      <p className={`text-sm font-medium ${formData.role === 'provider' ? 'text-[#0044CC]' : 'text-gray-600'}`}>{t('provider')}</p>
                    </button>
                  </div>
                </div>
                <div className="flex items-start gap-3 pt-2">
                  <Checkbox id="terms" checked={formData.acceptedTerms}
                    onCheckedChange={(checked) => setFormData({ ...formData, acceptedTerms: checked })}
                    data-testid="terms-checkbox" />
                  <label htmlFor="terms" className="text-sm text-gray-600 leading-tight cursor-pointer">
                    Ich habe die <Link to="/agb" className="text-[#0044CC] hover:underline" target="_blank">AGB</Link>
                    {' '}und <Link to="/datenschutz" className="text-[#0044CC] hover:underline" target="_blank">Datenschutzbestimmungen</Link> gelesen und akzeptiert
                  </label>
                </div>
                <Button type="submit" className="w-full h-11 bg-[#0044CC] hover:bg-[#003399] text-white font-medium rounded-full"
                  disabled={loading || !formData.acceptedTerms} data-testid="submit-btn">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                    formData.role === 'provider' ? <>{t('continueSetup')} <ChevronRight className="w-4 h-4 ml-1" /></> : t('register')
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center mt-6 text-gray-500">
            {t('hasAccount')}
            <button onClick={() => setIsLogin(true)} className="ml-1 text-[#0044CC] hover:underline font-medium">
              {t('login')}
            </button>
          </p>
        </div>
      </div>
    );
  }

  // Registration Form - Step 2 (Professional Details)
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <Logo className="h-12 w-12" />
            <span className="font-bold text-2xl" style={{ fontFamily: 'Chivo, sans-serif' }}>Infirmly Swiss</span>
          </Link>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {t('completeProfile')}
          </h1>
          <p className="text-gray-500 mt-2">{t('completeProfileDesc')}</p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center">
              <Check className="w-4 h-4" />
            </div>
            <div className="w-12 h-1 bg-[#0044CC] rounded"></div>
            <div className="w-8 h-8 rounded-full bg-[#0044CC] text-white flex items-center justify-center text-sm font-medium">2</div>
          </div>
        </div>

        <Card className="border-gray-200 shadow-sm">
          <CardContent className="p-6">
            <div className="space-y-6">
              {/* Profile Picture */}
              <div className="flex flex-col items-center">
                <div className="relative w-24 h-24 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-[#0044CC] transition-colors overflow-hidden"
                  onClick={() => fileInputRef.current?.click()}>
                  {profilePreview ? (
                    <img src={profilePreview} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <Camera className="w-8 h-8 text-gray-400" />
                  )}
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" data-testid="profile-picture-input" />
                <p className="text-sm text-gray-500 mt-2">Profilbild hochladen</p>
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone">{t('phone')}</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input id="phone" type="tel" placeholder="+41 79 123 45 67" value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="h-11 pl-10" data-testid="phone-input" />
                </div>
              </div>

              {/* Company Name */}
              <div>
                <Label htmlFor="company">Firmenname</Label>
                <div className="relative mt-1">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input id="company" placeholder="z.B. Müller Consulting GmbH" value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="h-11 pl-10" data-testid="company-input" />
                </div>
              </div>

              {/* Address */}
              <div>
                <Label>Adresse</Label>
                <div className="mt-1">
                  <AddressAutocomplete
                    value={formData.address}
                    onChange={(val) => setFormData({ ...formData, address: val })}
                    onSelect={(suggestion) => setFormData({ ...formData, address: suggestion.display_name })}
                    placeholder="Strasse, Hausnummer, Stadt, PLZ"
                    data-testid="address-input"
                  />
                </div>
              </div>

              {/* City & Postal Code */}
              <div>
                <Label>Stadt / PLZ</Label>
                <div className="mt-1">
                  <SwissPostAutocomplete
                    value={formData.postal_code ? `${formData.postal_code} ${formData.city}` : ''}
                    onChange={() => {}}
                    onPlaceSelect={(data) => {
                      if (data) {
                        setFormData({ ...formData, city: data.city, postal_code: data.postal_code, canton: data.canton });
                      } else {
                        setFormData({ ...formData, city: '', postal_code: '', canton: '' });
                      }
                    }}
                    placeholder="PLZ oder Stadt eingeben..."
                  />
                </div>
                {formData.city && (
                  <p className="text-xs text-green-600 mt-1">✓ {formData.postal_code} {formData.city}, Kanton {formData.canton}</p>
                )}
              </div>

              {/* Canton */}
              <div>
                <Label>Kanton</Label>
                <Select value={formData.canton} onValueChange={(v) => setFormData({ ...formData, canton: v })}>
                  <SelectTrigger className="mt-1 h-11" data-testid="canton-select">
                    <SelectValue placeholder="Wird automatisch ausgefüllt" />
                  </SelectTrigger>
                  <SelectContent>
                    {SWISS_CANTONS.map((canton) => (
                      <SelectItem key={canton.code} value={canton.code}>{canton.name} ({canton.code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Automatisch bei Stadtauswahl</p>
              </div>

              {/* Categories */}
              <div>
                <Label>Dienstleistungskategorien</Label>
                <p className="text-sm text-gray-500 mb-3">Wählen Sie Ihre Fachgebiete aus</p>
                <ScrollArea className="h-48 border rounded-lg p-3">
                  <div className="grid grid-cols-1 gap-2">
                    {categories.map((cat) => {
                      const IconComponent = iconMap[cat.icon] || Briefcase;
                      const isSelected = formData.selected_categories.includes(cat.id);
                      return (
                        <div key={cat.id}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${isSelected ? 'bg-blue-50 border border-[#0044CC]' : 'bg-gray-50 border border-transparent hover:bg-gray-100'}`}
                          onClick={() => toggleCategory(cat.id)} data-testid={`category-select-${cat.id}`}>
                          <Checkbox checked={isSelected} className="pointer-events-none" />
                          <IconComponent className={`w-5 h-5 ${isSelected ? 'text-[#0044CC]' : 'text-gray-400'}`} />
                          <span className={`text-sm ${isSelected ? 'text-[#0044CC] font-medium' : 'text-gray-700'}`}>
                            {cat[`name_${language}`] || cat.name_en || cat.name}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
                {formData.selected_categories.length > 0 && (
                  <p className="text-sm text-[#0044CC] mt-2">{formData.selected_categories.length} Kategorie(n) ausgewählt</p>
                )}
              </div>

              {/* Certifications */}
              <div>
                <Label htmlFor="certifications">Zertifizierungen & Qualifikationen</Label>
                <div className="relative mt-1">
                  <Award className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <Textarea id="certifications" placeholder="z.B. CPA, MBA, Fachausweis, Diplome..."
                    value={formData.certifications}
                    onChange={(e) => setFormData({ ...formData, certifications: e.target.value })}
                    className="pl-10 min-h-[80px]" data-testid="certifications-input" />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1 h-11 rounded-full" data-testid="back-btn">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
                </Button>
                <Button onClick={handleFinalSubmit}
                  className="flex-1 h-11 bg-[#0044CC] hover:bg-[#003399] text-white font-medium rounded-full"
                  disabled={loading} data-testid="complete-registration-btn">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t('register')} <Check className="w-4 h-4 ml-1" /></>}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthPage;
